/**
 * CO2 Badge - Frontend Script
 * Calculates and displays the CO2 footprint of the current page
 * Based on CO2.js by The Green Web Foundation
 * 
 * HYBRID MEASUREMENT APPROACH (Crowd-Sourced):
 * - First visitor who scrolls to badge measures page size (Performance API, no auto-scroll)
 * - Value is cached centrally in WordPress transients (7 days)
 * - All other visitors use cached value instantly (zero overhead)
 * - No automatic scrolling, no localStorage, no per-user measurement
 * 
 * @package WebsiteCarbonTracker
 */

/* global requestAnimationFrame, document, window, fetch, console, wctData, IntersectionObserver */
import { co2 } from '@tgwf/co2'

class WctBadge {
  constructor (element) {
    this.element = element
    
    // Initialize CO2.js with Sustainable Web Design Model + Segment Results
    this.co2Instance = new co2({
      model: 'swd',        // Sustainable Web Design Model
      results: 'segment'   // Enable segment-level breakdown
    })
    
    this.isVisible = true
    this.isInViewport = false
    this.hasTriedMeasurement = false

    // DOM references
    this.amountElement = element.querySelector('[data-wct-amount]')
    this.comparisonElement = element.querySelector('[data-wct-comparison]')
    this.pageSizeElement = element.querySelector('[data-wct-page-size]')
    this.hostingElement = element.querySelector('[data-wct-hosting]')
    this.toggleButton = element.querySelector('[data-wct-toggle]')
    this.detailsElement = element.querySelector('[data-wct-details]')
    this.closeButton = element.querySelector('[data-wct-close]')

    this.init()
  }

  async init () {
    try {
      this.setupEventListeners()
      this.setupIntersectionObserver()
      await this.calculateCo2()
      this.show()
    } catch (error) {
      console.error('WCT: Initialization failed:', error)
      this.showError()
    }
  }

  setupIntersectionObserver () {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isInViewport = true
          if (!this.hasTriedMeasurement) {
            this.onBadgeVisible()
          }
        }
      })
    }, {
      threshold: 0.1
    })

    observer.observe(this.element)
  }

  async onBadgeVisible () {
    if (!this.hasTriedMeasurement) {
      console.log('👀 WCT: Badge is now visible, starting measurement...')
      await this.performMeasurement()
    }
  }

  setupEventListeners () {
    // Details Toggle
    if (this.toggleButton && this.detailsElement) {
      this.toggleButton.addEventListener('click', () => this.toggleDetails())
    }

    // Close Button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.hide())
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide()
      }
    })

    // Restore hidden state from localStorage
    const isHidden = localStorage.getItem('wct-badge-hidden') === 'true'
    if (isHidden) {
      this.hide(false)
    }
  }

  async calculateCo2 () {
    try {
      // Step 1: Try to get cached page size from WordPress
      const cachedData = await this.getCachedPageSize()
      
      if (cachedData && cachedData.pageSize) {
        const kb = Math.round(cachedData.pageSize / 1024)
        console.log('✅ WCT: Using cached value from server')
        console.log(`   Page Size: ${kb} KB`)
        
        const options = {
          firstVisitPercentage: 0.75,
          returnVisitPercentage: 0.25,
          dataReloadRatio: 0.02
        }
        
        const result = this.co2Instance.perVisitTrace(
          cachedData.pageSize,
          cachedData.isGreenHost || false,
          options
        )
        
        this.displayResults(result, cachedData.pageSize, cachedData.isGreenHost || false)
        this.hasTriedMeasurement = true
        
        console.log(`   CO2: ${(result.co2?.total || 0).toFixed(2)}g per visit`)
        console.log(`   Green Host: ${cachedData.isGreenHost ? 'Yes ♻️' : 'No'}`)
        return
      }
      
      // Step 2: No cached value - wait for badge visibility
      console.log('⏳ WCT: No cached value, waiting for badge visibility...')
      
      if (this.isInViewport) {
        await this.performMeasurement()
      }
      
    } catch (error) {
      console.error('❌ WCT: Calculation failed:', error)
      this.showError()
    }
  }
  
  async getCachedPageSize () {
    if (typeof wctData === 'undefined') {
      console.warn('WCT: Data object not available')
      return null
    }
    
    try {
      const formData = new FormData()
      formData.append('action', 'wct_get_page_size')
      formData.append('page_id', wctData.pageId)
      formData.append('nonce', wctData.nonce)
      
      const response = await fetch(wctData.ajaxUrl, {
        method: 'POST',
        body: formData
      })
      
      const data = await response.json()
      
      if (data.success && data.data.cached) {
        return {
          pageSize: data.data.pageSize,
          isGreenHost: data.data.isGreenHost || false
        }
      }
      
      return null
    } catch (error) {
      console.error('WCT: Failed to get cached data:', error)
      return null
    }
  }
  
  async performMeasurement () {
    if (this.hasTriedMeasurement) return
    this.hasTriedMeasurement = true
    
    console.log('📊 WCT: Performing measurement...')
    
    try {
      const pageSize = await this.calculatePageSize()
      const kb = Math.round(pageSize / 1024)
      console.log(`   Measured: ${kb} KB`)
      
      // Save to WordPress
      const savedData = await this.savePageSize(pageSize)
      console.log('   ✅ Saved to WordPress (7 days cache)')
      
      // Calculate CO2
      const isGreenHost = savedData.isGreenHost || false
      const options = {
        firstVisitPercentage: 0.75,
        returnVisitPercentage: 0.25,
        dataReloadRatio: 0.02
      }
      
      const result = this.co2Instance.perVisitTrace(pageSize, isGreenHost, options)
      this.displayResults(result, pageSize, isGreenHost)
      
      console.log(`   CO2: ${(result.co2?.total || 0).toFixed(2)}g per visit`)
      console.log(`   Green Host: ${isGreenHost ? 'Yes ♻️' : 'No'}`)
      console.log('   All future visitors will use this cached value!')
    } catch (error) {
      console.error('❌ WCT: Measurement failed:', error)
      this.showError()
    }
  }
  
  async calculatePageSize () {
    return new Promise((resolve) => {
      const measure = async () => {
        console.log('   ⏱️  Waiting for page to settle (dynamic imports, lazy images)...')
        await this.waitForPageSettled(2000, 12000)

        const pageSize = this.getPageSize()
        resolve(pageSize)
      }

      if (document.readyState === 'complete') {
        measure()
      } else {
        window.addEventListener('load', () => measure(), { once: true })
      }
    })
  }

  async waitForPageSettled (idleMs = 2000, maxWaitMs = 12000) {
    return new Promise((resolve) => {
      let timer = null
      let resolved = false

      const finish = (reason) => {
        if (resolved) return
        resolved = true
        try { observer.disconnect() } catch (_) {}
        clearTimeout(timer)
        console.log(`   ✅ Page settled (${reason})`)
        resolve()
      }

      const resetTimer = () => {
        clearTimeout(timer)
        timer = setTimeout(() => finish(`idle for ${idleMs}ms`), idleMs)
      }

      let observer
      try {
        observer = new PerformanceObserver((list) => {
          if (list.getEntries().length > 0) {
            resetTimer()
          }
        })
        observer.observe({ type: 'resource', buffered: false })
      } catch (_) {
        // PerformanceObserver not supported — fall back to fixed delay
        setTimeout(resolve, idleMs)
        return
      }

      // Start idle countdown immediately (handles case where no new resources load)
      resetTimer()
      // Absolute ceiling
      setTimeout(() => finish('max wait reached'), maxWaitMs)
    })
  }

  getPageSize () {
    let totalSize = 0
    let largestResources = []

    const resources = performance.getEntriesByType('resource')

    if (resources && resources.length > 0) {
      const resourcesWithSize = []
      let cachedCount = 0
      let unknownCount = 0

      resources.forEach(resource => {
        let size = 0

        if (resource.transferSize > 0) {
          // Normal network transfer (includes response headers + compressed body)
          size = resource.transferSize
        } else if (resource.encodedBodySize > 0) {
          // Served from browser cache — transferSize is 0 but body is known
          // Count it: the first-ever visitor downloaded this resource
          size = resource.encodedBodySize
          cachedCount++
        } else {
          // Cross-origin resource without Timing-Allow-Origin header:
          // browser hides all timing/size data. Cannot measure.
          unknownCount++
          return
        }

        totalSize += size

        resourcesWithSize.push({
          url: resource.name,
          size: size,
          type: this.getResourceType(resource)
        })
      })

      if (cachedCount > 0) {
        console.log(`   ℹ️  ${cachedCount} cached resources (using body size)`)
      }
      if (unknownCount > 0) {
        console.warn(`   ⚠️  ${unknownCount} cross-origin resources with unknown size (no Timing-Allow-Origin)`)
      }

      // Top 5 largest
      largestResources = resourcesWithSize
        .sort((a, b) => b.size - a.size)
        .slice(0, 5)

      this.largestResources = largestResources

      // HTML document: use the Navigation Timing API for the actual transfer size
      // (avoids measuring the current DOM which is uncompressed and may include
      // dynamically injected content, inflating the result by 5–10×)
      const navEntry = performance.getEntriesByType('navigation')[0]
      let htmlSize = 0
      if (navEntry) {
        htmlSize = navEntry.transferSize > 0
          ? navEntry.transferSize
          : navEntry.encodedBodySize  // cached page
      }
      totalSize += htmlSize

      console.log('📊 Performance API Measurement:')
      console.log(`   Total: ${(totalSize / 1024).toFixed(0)} KB`)
      console.log(`   Resources: ${resources.length} (${cachedCount} from cache, ${unknownCount} unknown)`)
      console.log(`   HTML document: ${(htmlSize / 1024).toFixed(0)} KB`)

      if (largestResources.length > 0) {
        console.log('🔍 Top 5 largest:')
        largestResources.forEach((res, idx) => {
          const kb = (res.size / 1024).toFixed(0)
          const warning = res.size > 512000 ? '⚠️ ' : ''
          console.log(`   ${idx + 1}. ${warning}${res.type}: ${kb} KB`)
        })
      }
    }

    return totalSize
  }

  getResourceType (resource) {
    const url = resource.name.toLowerCase()
    const type = resource.initiatorType
    
    if (type === 'script' || url.match(/\.js$/)) return 'JavaScript'
    if (type === 'link' || type === 'css' || url.match(/\.css$/)) return 'CSS'
    if (type === 'img' || url.match(/\.(jpg|jpeg|png|gif|webp|avif)$/)) return 'Image'
    if (url.match(/\.svg$/)) return 'SVG'
    if (url.match(/\.(woff2?|ttf|otf|eot)$/)) return 'Font'
    if (url.match(/\.(mp4|webm|ogg)$/)) return 'Video'
    if (url.match(/\.(mp3|wav|ogg)$/)) return 'Audio'
    
    return 'Other'
  }

  async savePageSize (pageSize) {
    if (typeof wctData === 'undefined') {
      console.warn('WCT: Cannot save - data not available')
      return {}
    }
    
    try {
      const formData = new FormData()
      formData.append('action', 'wct_save_page_size')
      formData.append('page_id', wctData.pageId)
      formData.append('page_size', pageSize)
      formData.append('nonce', wctData.nonce)
      
      if (this.largestResources && this.largestResources.length > 0) {
        formData.append('largest_resources', JSON.stringify(this.largestResources))
      }
      
      const response = await fetch(wctData.ajaxUrl, {
        method: 'POST',
        body: formData
      })
      
      const data = await response.json()
      
      if (data.success) {
        console.log('WCT: Saved successfully')
        return data.data || {}
      } else {
        console.warn('WCT: Save failed:', data.data?.message)
        return {}
      }
    } catch (error) {
      console.error('WCT: Save error:', error)
      return {}
    }
  }

  displayResults (result, pageSize, isGreenHost) {
    // Extract CO2 value
    let co2Grams = 0
    if (typeof result === 'number') {
      co2Grams = result
    } else if (result?.co2?.total) {
      co2Grams = result.co2.total
    }
    
    const co2Display = co2Grams < 1 ? co2Grams.toFixed(2) : co2Grams.toFixed(1)
    
    // Display
    if (this.amountElement) {
      this.amountElement.innerHTML = co2Display
    }
    
    const comparisonText = this.generateComparisonText(co2Grams, pageSize)
    if (this.comparisonElement) {
      this.comparisonElement.textContent = comparisonText
    }
    
    if (this.pageSizeElement) {
      const kb = (pageSize / 1024).toFixed(0)
      this.pageSizeElement.textContent = `${kb} KB`
    }
    
    if (this.hostingElement) {
      this.hostingElement.textContent = isGreenHost ? 'Green Hosting' : 'Standard'
      if (isGreenHost) {
        this.hostingElement.classList.add('wct-badge__metricValue--green')
      }
    }
  }

  generateComparisonText (co2Grams, pageSize) {
    const mb = pageSize / 1024 / 1024
    
    if (mb < 0.5) {
      return 'Very efficient - smaller than ~90% of all websites worldwide'
    } else if (mb < 1.0) {
      return 'Efficient - smaller than ~70% of all websites worldwide'
    } else if (mb < 2.0) {
      return 'Below average - smaller than ~36% of all websites'
    } else if (mb < 2.2) {
      return 'Near global average (2.2 MB)'
    } else if (mb < 4.0) {
      return 'Above average - larger than most websites'
    } else {
      return 'Very large - larger than ~88% of all websites worldwide'
    }
  }

  toggleDetails () {
    if (!this.detailsElement || !this.toggleButton) return
    
    const isExpanded = this.toggleButton.getAttribute('aria-expanded') === 'true'
    const newState = !isExpanded
    
    this.toggleButton.setAttribute('aria-expanded', newState)
    this.detailsElement.setAttribute('aria-hidden', !newState)
    
    if (newState) {
      this.detailsElement.focus()
    }
  }

  show (animate = true) {
    this.isVisible = true
    this.element.classList.remove('wct-badge--hidden')
    
    if (!animate) {
      this.element.style.transition = 'none'
      requestAnimationFrame(() => {
        this.element.style.transition = ''
      })
    }
    
    localStorage.removeItem('wct-badge-hidden')
  }

  hide (animate = true) {
    this.isVisible = false
    this.element.classList.add('wct-badge--hidden')
    
    if (!animate) {
      this.element.style.transition = 'none'
      requestAnimationFrame(() => {
        this.element.style.transition = ''
      })
    }
    
    localStorage.setItem('wct-badge-hidden', 'true')
    
    if (this.toggleButton && this.detailsElement) {
      this.toggleButton.setAttribute('aria-expanded', 'false')
      this.detailsElement.setAttribute('aria-hidden', 'true')
    }
  }

  showError () {
    if (this.amountElement) {
      this.amountElement.innerHTML = '?'
    }
    if (this.comparisonElement) {
      this.comparisonElement.textContent = 'Calculation not possible'
    }
  }
}

// Auto-initialization
document.addEventListener('DOMContentLoaded', () => {
  const badges = document.querySelectorAll('[data-wct-badge]')
  badges.forEach(badge => new WctBadge(badge))
})

export default WctBadge
